<!--
VueButtonsGrid Component - Square Button Grid Selection Component

FEATURES:
=========
- 4-column grid of square buttons for selection
- Each button supports a title and optional SVG icon
- Severity levels per button (primary, secondary, warning, success, info, danger)
- 3 size options: sm (120px), md (180px), lg (260px)
- Search bar to filter options
- Auto-selects first available option when no value is set
- Supports validation with visual indicators
- Handles disabled state
- Emits both v-model and legacy input events

PROPS:
======
- itensSelect: Array of objects with {ID, Name, icon?, severity?, color?, borderColor?} structure
- elemId: Unique identifier for the grid element
- label: Label text displayed on the left
- size: Button size - 'sm' | 'md' | 'lg' (default: 'md')
- validation: Function that returns boolean for validation state
- ngRequired: Boolean/string indicating if field is required
- disabled: Boolean/string indicating if field is disabled
- modelValue: Current selected value (v-model)
- value: Legacy value prop for backward compatibility
- callback: Optional function to call when value changes
- searchEnabled: Boolean indicating if search bar should be displayed (default: false)
- columns: Number of columns per row (default: 4)
-->

<template>
  <div class="buttons-grid-container" :class="{ 'no-label': !hasLabel }">
    <div v-if="hasLabel" class="buttons-grid-label" :class="{
      'validation-warning': !hasValidation,
      'validation-success': hasValidation
    }">
      <label class="field-label" :class="{ 'label-disabled': isDisabled }">
        <i v-if="isRequired" class="pi pi-exclamation-triangle validation-icon-warning" v-show="!hasValidation"></i>
        <i v-if="isRequired" class="pi pi-check validation-icon-success" v-show="hasValidation"></i>
        <span v-if="isRequired" class="required-indicator">*</span>{{ label }}:
      </label>
    </div>
    <div class="buttons-grid-content" :id="'container_' + elemId">
      <div v-if="searchEnabled" class="search-bar-container">
        <IconField iconPosition="left">
          <InputIcon>
            <i class="pi pi-search" />
          </InputIcon>
          <InputText
            v-model="searchQuery"
            :placeholder="t('Procurar')"
            class="search-input"
            :disabled="isDisabled"
          />
        </IconField>
      </div>
      <div class="buttons-grid" :style="gridStyle">
        <button
          v-for="item in filteredItems"
          :key="item.ID"
          type="button"
          :disabled="isDisabled"
          :data-testid="`${elemId}-${item.ID}`"
          class="grid-button"
          :class="[
            `grid-button--${size}`,
            `grid-button--${getItemSeverity(item)}`,
            { 'grid-button--selected': item.ID === internalValue, 'grid-button--colored': !!item.color || !!item.borderColor }
          ]"
          :style="item.color || item.borderColor ? { '--grid-button-color': item.color || '', '--grid-button-border-color': item.borderColor || item.color || '' } : undefined"
          @click="onChangeSelect(item.ID)"
        >
          <!-- `item.icon` é SVG guardado no banco (so_item_attribute_option_icon_svg,
               so_service_category_icon_svg): conteúdo que um administrador cola
               num formulário e que aparece na tela de todo mundo. -->
          <span v-if="item.icon" class="grid-button__icon" :class="`grid-button__icon--${size}`" v-html="sanitizeSvgIcon(item.icon)"></span>
          <span class="grid-button__title" :class="`grid-button__title--${size}`">{{ item.Name }}</span>
          <span v-if="item.description" class="grid-button__description" :class="`grid-button__description--${size}`">{{ item.description }}</span>
          <i v-if="item.ID === internalValue" class="pi pi-check grid-button__check"></i>
        </button>
      </div>
      <div v-if="searchEnabled && filteredItems.length === 0" class="no-results">
        {{ t('No results found') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import { sanitizeSvgIcon } from '../utils/sanitizeSvgIcon';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';

interface GridSelectItem {
  ID: string | number;
  Name: string;
  icon?: string;
  severity?: string;
  description?: string;
  color?: string;
  borderColor?: string;
}

const { t } = useI18n();

const props = defineProps({
  callback: Function as any,

  // Array of objects: { ID, Name, icon? (SVG string), severity? ('primary'|'secondary'|'warning'|'success'|'info'|'danger'), description? (string), color? (hex string) }
  itensSelect: {
    type: Array as () => GridSelectItem[],
    default: () => []
  },

  elemId: String,
  placeholder: String,
  label: String,

  // Button size: 'sm' (120px), 'md' (180px), 'lg' (260px)
  size: {
    type: String,
    default: 'md',
    validator: (val: string) => ['sm', 'md', 'lg'].includes(val)
  },

  // Number of columns in the grid
  columns: {
    type: Number,
    default: 4
  },

  validation: {
    type: Function as any,
    default: () => true
  },

  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  disabled: {
    type: [String, Boolean],
    default: false
  },

  modelValue: [String, Number] as any,
  value: [String, Number] as any,

  searchEnabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  'input': [value: string | number];
}>();

const internalValue = ref<string | number>(props.modelValue ?? props.value ?? '');
const searchQuery = ref<string>('');

const hasLabel = computed((): boolean => {
  return props.label !== null &&
         props.label !== undefined &&
         props.label !== '' &&
         (props.label as string).trim() !== '';
});

const filteredItems = computed((): GridSelectItem[] => {
  if (!props.searchEnabled || !searchQuery.value) {
    return props.itensSelect;
  }

  const query = searchQuery.value.toLowerCase();
  return props.itensSelect.filter((item: GridSelectItem) =>
    (item.Name && item.Name.toLowerCase().includes(query)) ||
    (item.description && item.description.toLowerCase().includes(query))
  );
});

const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${props.columns}, 1fr)`
}));

const getItemSeverity = (item: GridSelectItem): string => {
  return item.severity || 'secondary';
};

const autoSelectFirstValue = (): void => {
  if ((!internalValue.value || internalValue.value === '') &&
      props.itensSelect &&
      props.itensSelect.length > 0) {
    const firstItem = props.itensSelect.find((item: GridSelectItem) => item.ID && item.ID !== '');
    if (firstItem) {
      internalValue.value = firstItem.ID;
    }
  }
};

const onChangeSelect = (id: string | number): void => {
  if (!isDisabled.value) {
    internalValue.value = id;
  }
};

watch(() => props.modelValue, (newVal: string | number | undefined) => {
  internalValue.value = newVal ?? '';
});

watch(() => props.value, (newVal: string | number | undefined) => {
  internalValue.value = newVal ?? '';
});

watch(internalValue, (newVal: string | number) => {
  emit('update:modelValue', newVal);
  emit('input', newVal);
  if (props.callback) {
    (props.callback as () => void)();
  }
});

watch(() => props.itensSelect, () => {
  nextTick(() => {
    autoSelectFirstValue();
  });
}, { deep: true });

nextTick(() => {
  autoSelectFirstValue();
});
</script>

<style scoped>
/* Main Container */
.buttons-grid-container {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  align-items: flex-start;
}

.buttons-grid-container.no-label {
  gap: 0;
}

.buttons-grid-container.no-label .buttons-grid-content {
  width: 100%;
}

/* Label Section */
.buttons-grid-label {
  flex: 0 0 20%;
  min-width: 120px;
  text-align: right;
  padding-top: 5px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.label-disabled {
  opacity: 0.5;
}

.required-indicator {
  color: #f39c12;
  margin-right: 2px;
}

.validation-icon-warning {
  color: #f39c12;
  font-size: 0.875rem;
}

.validation-icon-success {
  color: #00a65a;
  font-size: 0.875rem;
}

/* Content Section */
.buttons-grid-content {
  flex: 1;
  min-width: 0;
}

/* Search Bar */
.search-bar-container {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
}

/* Grid Layout */
.buttons-grid {
  display: grid;
  gap: 8px;
}

/* No Results */
.no-results {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
}

/* Square Button Base */
.grid-button {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8f9fa;
  padding: 12px 8px;
  overflow: hidden;
}

.grid-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.grid-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Sizes */
.grid-button--sm {
  min-height: 80px;
  font-size: 0.8rem;
}

.grid-button--md {
  min-height: 100px;
  font-size: 1.1rem;
}

.grid-button--lg {
  min-height: 140px;
  font-size: 1.4rem;
}

/* Icon Sizes */
.grid-button__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.grid-button__icon--sm {
  width: 40px;
  height: 40px;
}

.grid-button__icon--md {
  width: 60px;
  height: 60px;
}

.grid-button__icon--lg {
  width: 90px;
  height: 90px;
}

.grid-button__icon :deep(svg) {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.grid-button__icon :deep(svg path),
.grid-button__icon :deep(svg circle),
.grid-button__icon :deep(svg rect),
.grid-button__icon :deep(svg polygon),
.grid-button__icon :deep(svg line),
.grid-button__icon :deep(svg polyline),
.grid-button__icon :deep(svg ellipse) {
  fill: currentColor;
  stroke: currentColor;
}

/* Title */
.grid-button__title {
  text-align: center;
  word-break: break-word;
  line-height: 1.2;
  font-weight: 500;
}

.grid-button__title--sm {
  font-size: 0.8rem;
}

.grid-button__title--md {
  font-size: 1.1rem;
}

.grid-button__title--lg {
  font-size: 1.4rem;
}

/* Description */
.grid-button__description {
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  opacity: 1;
}

.grid-button__description--sm {
  font-size: 0.65rem;
}

.grid-button__description--md {
  font-size: 0.8rem;
}

.grid-button__description--lg {
  font-size: 1rem;
}

/* Check Icon (selected indicator) */
.grid-button__check {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.85rem;
}

/* Severity: Primary */
.grid-button--primary {
  border-color: var(--p-primary-color);
  color: var(--p-primary-color);
  background: var(--p-primary-50, #EFF6FF);
}

.grid-button--primary.grid-button--selected {
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color, white);
}

/* Severity: Secondary */
.grid-button--secondary {
  border-color: #6B7280;
  color: #6B7280;
  background: #F9FAFB;
}

.grid-button--secondary.grid-button--selected {
  background: #6B7280;
  color: white;
}

/* Severity: Success */
.grid-button--success {
  border-color: #22C55E;
  color: #22C55E;
  background: #F0FDF4;
}

.grid-button--success.grid-button--selected {
  background: #22C55E;
  color: white;
}

/* Severity: Warning */
.grid-button--warning {
  border-color: #F59E0B;
  color: #F59E0B;
  background: #FFFBEB;
}

.grid-button--warning.grid-button--selected {
  background: #F59E0B;
  color: white;
}

/* Severity: Info */
.grid-button--info {
  border-color: #06B6D4;
  color: #06B6D4;
  background: #ECFEFF;
}

.grid-button--info.grid-button--selected {
  background: #06B6D4;
  color: white;
}

/* Severity: Danger */
.grid-button--danger {
  border-color: #EF4444;
  color: #EF4444;
  background: #FEF2F2;
}

.grid-button--danger.grid-button--selected {
  background: #EF4444;
  color: white;
}

/* Severity: Outline (transparent bg, just border) */
.grid-button--outline {
  border-color: #D1D5DB;
  color: #374151;
  background: transparent;
}

.grid-button--outline.grid-button--selected {
  border-color: #3B82F6;
  background: #3B82F6;
  color: white;
}

/* Per-item color: when an item carries `color`, the button is accented with that
   exact hex. Driven by the `--grid-button-color` custom property set inline on the
   button; items without a color are unaffected (the severity styles above apply).
   NOTE: double-class selectors (higher specificity) + placed AFTER the severity
   rules so the per-item color always wins over any severity styling. */
.grid-button.grid-button--colored {
  border-color: var(--grid-button-border-color, var(--grid-button-color));
  color: var(--grid-button-color);
  background: var(--p-surface-0, #fff);
}

.grid-button.grid-button--colored.grid-button--selected {
  background: var(--grid-button-color);
  border-color: var(--grid-button-border-color, var(--grid-button-color));
  color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .buttons-grid-container {
    flex-direction: column;
    gap: 10px;
  }

  .buttons-grid-label {
    flex: 1 1 100%;
    text-align: left;
    padding-top: 0;
  }

  .field-label {
    font-size: 0.875rem;
  }

  .buttons-grid-content {
    width: 100%;
  }

  .buttons-grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

@media (max-width: 480px) {
  .buttons-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 6px;
  }
}
</style>
